import React, { useState } from 'react';

/**
 * InformeCRMButton
 * --------------------------------------------------------
 * Botón flotante (position: fixed, abajo-dcha) que genera y descarga
 * un informe PDF idéntico en maquetación al "Informe CRM Dimoauto",
 * usando los datos en tiempo real que el Dashboard ya trae en `data`.
 *
 * No añade dependencias al package.json: carga jsPDF + autoTable + Chart.js
 * dinámicamente desde CDN solo cuando se pulsa el botón.
 *
 * Uso en Dashboard.jsx:
 *     import InformeCRMButton from './InformeCRMButton';
 *     ...
 *     <InformeCRMButton data={data} />
 */

// ---------- Loader de scripts en CDN (solo una vez) ----------
const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    document.body.appendChild(s);
  });

const ensureLibs = async () => {
  if (!window.jspdf) {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
  }
  if (!(window.jspdf && window.jspdf.jsPDF && window.jspdf.jsPDF.API.autoTable)) {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
  }
  if (!window.Chart) {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js');
  }
};

// ---------- Utilidades numéricas / texto ----------
const pct = (num, den) => (den > 0 ? (num / den) * 100 : 0);
const fmtPct = (n) => `${n.toFixed(1)}%`;
const mesES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

// ---------- Paleta (pastel suave, tipo el PDF de referencia) ----------
const PALETTE = {
  azulDimo: [0, 81, 135],       // #005187
  azulClaro: [132, 182, 244],   // #7cc4e8
  amarillo: [245, 197, 66],
  verde: [66, 167, 110],
  naranja: [235, 139, 72],
  grisTexto: [60, 60, 60],
  grisSuave: [130, 140, 155],
  bandaAzul: [230, 239, 250],
  bandaAmbar: [254, 243, 199],
  lineaCabecera: [195, 210, 228],
};

// ---------- Renderizado de una gráfica en canvas oculto ----------
const renderChartToDataURL = async (cfg, w = 520, h = 300) => {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.style.position = 'fixed';
  canvas.style.left = '-10000px';
  canvas.style.top = '-10000px';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  // fondo blanco (por si el PDF lo necesita)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  const chart = new window.Chart(ctx, { ...cfg, options: { ...(cfg.options || {}), animation: false, responsive: false, devicePixelRatio: 2 } });
  await new Promise((r) => setTimeout(r, 60)); // pequeño settle
  const url = chart.toBase64Image('image/png', 1);
  chart.destroy();
  canvas.remove();
  return url;
};

// ---------- Helpers de layout en jsPDF ----------
const addPageFrame = (doc, pageNum) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Banda superior fina azul
  doc.setFillColor(...PALETTE.azulDimo);
  doc.rect(0, 0, W, 4, 'F');
  doc.setFillColor(245, 197, 66);
  doc.rect(0, 4, W, 1.6, 'F');

  // Cabecera textual
  doc.setFontSize(8);
  doc.setTextColor(...PALETTE.azulDimo);
  doc.setFont('helvetica', 'bold');
  doc.text('GRUPO DIMOAUTO', 14, 11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...PALETTE.grisSuave);
  const hoy = new Date();
  doc.text(`Informe CRM — ${hoy.getDate()} de ${mesES[hoy.getMonth()]} de ${hoy.getFullYear()}`, W - 14, 11, { align: 'right' });

  // Pie
  doc.setDrawColor(225, 229, 235);
  doc.line(14, H - 12, W - 14, H - 12);
  doc.setFontSize(8);
  doc.setTextColor(...PALETTE.grisSuave);
  doc.text('Documento confidencial — Uso interno', 14, H - 7);
  doc.text(`Pagina ${pageNum}`, W - 14, H - 7, { align: 'right' });
};

const sectionTitle = (doc, y, color, text) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(0.8);
  doc.line(14, y, 196, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...PALETTE.azulDimo);
  doc.text(text, 14, y + 7);
};

const paragraph = (doc, y, text, opts = {}) => {
  doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
  doc.setFontSize(opts.size || 10);
  doc.setTextColor(...(opts.color || PALETTE.grisTexto));
  const lines = doc.splitTextToSize(text, opts.maxWidth || 182);
  doc.text(lines, 14, y, { lineHeightFactor: 1.35, align: opts.align || 'justify', maxWidth: opts.maxWidth || 182 });
  return y + lines.length * (opts.size ? opts.size * 0.42 : 4.2);
};

// ---------- Generador principal ----------
const generarInforme = async (dashboardData) => {
  await ensureLibs();
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const d = dashboardData || {};
  const email = d.emailLeads || { origenes: {} };
  const cust = d.customerLeads || { origenes: {} };
  const meta = d.metaLeads || { origenes: {} };
  const auto = d.autoLeads || {};
  const admin = d.crmAdmin || {};
  const com = d.crmComerciales || {};
  const centros = d.centros || {};

  // ===== Cálculos derivados =====
  const leadsCartera = admin.leads || 0;
  const comercialesActivos = com.activos || 0;
  const tasaConv = com.tasaConversion || 0;
  // tiempoRespuesta del dashboard viene como "X min Y seg Z mil" — extraemos minutos para el KPI principal
  const tiempoRespTexto = com.tiempoRespuesta || '-';
  const minutosRespMatch = String(tiempoRespTexto).match(/(\d+)\s*min/);
  const minutosResp = minutosRespMatch ? parseInt(minutosRespMatch[1], 10) : null;
  const kpiTiempo = minutosResp !== null ? `${minutosResp} min` : (tiempoRespTexto && tiempoRespTexto !== '-' ? tiempoRespTexto : 'N/A');

  const vn = admin.vn || 0;
  const vo = admin.vo || 0;
  const pctVN = pct(vn, vn + vo);
  const pctVO = pct(vo, vn + vo);

  const autoVN = auto.leadsVN || 0;
  const autoVO = auto.leadsVO || 0;
  const pctAutoVN = pct(autoVN, autoVN + autoVO);
  const pctAutoVO = pct(autoVO, autoVN + autoVO);

  const emailMes = email.mes || 0;
  const custMes = cust.mes || 0;
  const metaMes = meta.mes || 0;
  const autoMes = auto.mes || 0;
  const totalMes = emailMes + custMes + metaMes;

  const emailSem = email.semana || 0;
  const custSem = cust.semana || 0;
  const metaSem = meta.semana || 0;
  const totalSem = emailSem + custSem + metaSem;

  const emailHoy = email.hoy || 0;
  const custHoy = cust.hoy || 0;
  const metaHoy = meta.hoy || 0;
  const totalHoy = emailHoy + custHoy + metaHoy;

  // Centros -> array ordenado por leads desc
  const centrosDef = [
    ['Dimoauto Hospitalet', centros.dimoautoHospitalet || 0],
    ['Dimoauto Molins',     centros.dimoautoMolins || 0],
    ['MAAM Hospitalet',     centros.maamHospitalet || 0],
    ['MAAM Cornella',       centros.maamCornella || 0],
    ['JP Carrion Hosp.',    centros.jpCarrionHospitalet || 0],
    ['JP Carrion Molins',   centros.jpCarrionMolins || 0],
    ['Dimoauto Flotas',     centros.dimoautoFlotas || 0],
  ];
  centrosDef.sort((a, b) => b[1] - a[1]);
  const centrosTotal = centrosDef.reduce((s, c) => s + c[1], 0);

  // ===== Pre-render de gráficas =====
  // Donut Email Leads origen
  const emailOrigenes = [
    ['Coches.net', email.origenes?.cochesnet || 0],
    ['Web Dimoauto', email.origenes?.webdimoauto || 0],
    ['Carnovo', email.origenes?.carnovo || 0],
    ['Wallapop', email.origenes?.wallapop || 0],
    ['Pruebas', email.origenes?.autoocasion || 0],
  ];
  const emailTotal = emailOrigenes.reduce((s, x) => s + x[1], 0);

  const custOrigenes = [
    ['Peugeot', cust.origenes?.peugeot || 0],
    ['Citroën', cust.origenes?.citroen || 0],
    ['Opel', cust.origenes?.opel || 0],
    ['Fiat', cust.origenes?.fiat || 0],
    ['Jeep', cust.origenes?.jeep || 0],
    ['Fiat Pro', cust.origenes?.fiatpro || 0],
    ['Abarth', cust.origenes?.abarth || 0],
    ['Leapmotor', cust.origenes?.leapmotor || 0],
  ];
  const custTotal = custOrigenes.reduce((s, x) => s + x[1], 0);

  const metaOrigenes = [
    ['Facebook', meta.origenes?.facebook || 0],
    ['Instagram', meta.origenes?.instagram || 0],
  ];
  const metaTotal = metaOrigenes.reduce((s, x) => s + x[1], 0);

  const pastel = ['#7cc4e8', '#f8b4b4', '#f5e08c', '#9fd1a0', '#c7b6e0', '#ffc89e', '#a4d4e8', '#d6c9a3'];

  const donut = (labels, data) => ({
    type: 'doughnut',
    data: {
      labels: labels.map((l, i) => `${l} (${fmtPct(pct(data[i], data.reduce((s, x) => s + x, 0)))})`),
      datasets: [{ data: data.length ? data : [1], backgroundColor: pastel, borderWidth: 1, borderColor: '#fff' }],
    },
    options: {
      plugins: {
        legend: { position: 'right', labels: { boxWidth: 10, font: { size: 10 } } },
        title: { display: false },
      },
      cutout: '55%',
    },
  });

  const barH = (labels, data) => ({
    type: 'bar',
    data: {
      labels,
      datasets: [{
        data,
        backgroundColor: pastel,
        borderRadius: 3,
      }],
    },
    options: {
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Leads activos por instalacion', font: { size: 12, weight: 'bold' } },
        datalabels: { anchor: 'end', align: 'end' },
      },
      scales: {
        x: { title: { display: true, text: 'Numero de leads', font: { size: 9 } }, grid: { color: '#eef0f4' } },
        y: { grid: { display: false } },
      },
    },
  });

  const barV = (labels, serieMes, serieSem) => ({
    type: 'bar',
    data: {
      labels,
      datasets: [
        { label: 'Este mes', data: serieMes, backgroundColor: '#a9c7e8' },
        { label: 'Esta semana', data: serieSem, backgroundColor: '#c7b6e0' },
      ],
    },
    options: {
      plugins: {
        legend: { position: 'top', align: 'end', labels: { boxWidth: 10, font: { size: 10 } } },
        title: { display: true, text: 'Leads por canal — Mes vs. Semana actual', font: { size: 12, weight: 'bold' } },
      },
      scales: {
        y: { beginAtZero: true, title: { display: true, text: 'Leads', font: { size: 9 } }, grid: { color: '#eef0f4' } },
        x: { grid: { display: false } },
      },
    },
  });

  const barMarca = {
    type: 'bar',
    data: {
      labels: custOrigenes.map(([l]) => l),
      datasets: [{
        data: custOrigenes.map(([, v]) => v),
        backgroundColor: pastel,
        borderRadius: 3,
      }],
    },
    options: {
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Customer Leads por marca', font: { size: 12, weight: 'bold' } },
      },
      scales: {
        y: { beginAtZero: true, title: { display: true, text: 'Leads', font: { size: 9 } }, grid: { color: '#eef0f4' } },
        x: { grid: { display: false } },
      },
    },
  };

  // Render todas las gráficas en paralelo
  const [imgDonutEmail, imgDonutCust, imgDonutMeta, imgDonutVNVO, imgBarH, imgBarV, imgBarMarca] = await Promise.all([
    renderChartToDataURL(donut(emailOrigenes.map(([l]) => l), emailOrigenes.map(([, v]) => v)), 420, 300),
    renderChartToDataURL(donut(custOrigenes.map(([l]) => l), custOrigenes.map(([, v]) => v)), 420, 300),
    renderChartToDataURL(donut(metaOrigenes.map(([l]) => l), metaOrigenes.map(([, v]) => v)), 420, 300),
    renderChartToDataURL(donut(['Vehiculo Nuevo (91.6%)', 'Vehiculo Ocasion (8.4%)'.replace('91.6', fmtPct(pctVN).replace('%', '')).replace('8.4', fmtPct(pctVO).replace('%', ''))], [vn, vo]), 420, 320),
    renderChartToDataURL(barH(centrosDef.map((c) => c[0]), centrosDef.map((c) => c[1])), 620, 340),
    renderChartToDataURL(barV(['Email Leads', 'Customer 360', 'Meta Leads', 'AutoLeads'], [emailMes, custMes, metaMes, autoMes], [emailSem, custSem, metaSem, auto.semana || 0]), 620, 320),
    renderChartToDataURL(barMarca, 620, 280),
  ]);

  // ================================================================
  // ============================ PÁGINA 1 ==========================
  // ================================================================
  addPageFrame(doc, 1);

  // Logo si está disponible
  try {
    // logotipo.png se sirve desde /public
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = '/logotipo.png';
    });
    const c = document.createElement('canvas');
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    c.getContext('2d').drawImage(img, 0, 0);
    const dataUrl = c.toDataURL('image/png');
    // Centrado
    const logoW = 42;
    const logoH = (img.naturalHeight / img.naturalWidth) * logoW;
    doc.addImage(dataUrl, 'PNG', (210 - logoW) / 2, 22, logoW, logoH);
  } catch (e) {
    // Fallback: título textual centrado
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...PALETTE.azulDimo);
    doc.text('Dimoauto', 105, 30, { align: 'center' });
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...PALETTE.azulDimo);
  const hoy = new Date();
  doc.text(`Informe CRM ${mesES[hoy.getMonth()][0].toUpperCase() + mesES[hoy.getMonth()].slice(1)} ${hoy.getFullYear()}`, 105, 55, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...PALETTE.grisSuave);
  doc.text('Resumen ejecutivo de actividad comercial', 105, 61, { align: 'center' });

  // Línea decorativa
  doc.setDrawColor(...PALETTE.azulDimo);
  doc.setLineWidth(0.6);
  doc.line(65, 66, 145, 66);

  // Sección 1
  sectionTitle(doc, 74, PALETTE.azulDimo, '1. Resumen ejecutivo');

  // Párrafos resumen (dinámicos)
  const canalMasActivo = emailMes >= custMes && emailMes >= metaMes ? 'web y portales' : custMes >= metaMes ? 'el portal Stellantis' : 'redes sociales';
  const centroTop = centrosDef[0] || ['-', 0];
  const pctCentroTop = pct(centroTop[1], centrosTotal);

  let y = 90;
  y = paragraph(doc, y,
    `A fecha de hoy, el sistema CRM de Grupo Dimoauto gestiona ${leadsCartera} clientes potenciales activos (leads), distribuidos entre todas nuestras instalaciones. Son personas que han mostrado interés en adquirir un vehículo y están siendo atendidas por nuestro equipo comercial.`
  );
  y += 3;
  y = paragraph(doc, y,
    `La tasa de conversión se sitúa en el ${tasaConv}%, ${tasaConv > 8 ? 'por encima de' : tasaConv >= 5 ? 'dentro de' : 'por debajo de'} la media del sector, que habitualmente oscila entre el 5% y el 8%. El sistema AutoLeads asigna cada nuevo contacto en ${kpiTiempo} de media, lo que mejora la experiencia del cliente y reduce el riesgo de perder oportunidades de venta.`
  );
  y += 3;
  y = paragraph(doc, y,
    `En cuanto al tipo de vehículo demandado, el ${fmtPct(pctVN)} de los clientes busca vehículo nuevo (VN), con ${fmtPct(pctVO)} interesado en ocasión (VO). Esta tendencia es consistente en todos los canales y refuerza la estrategia de potenciar la gama nueva Stellantis —Peugeot, Citroën, Opel, Fiat, Jeep, Abarth y Leapmotor— en las campañas digitales.`
  );
  y += 3;
  y = paragraph(doc, y,
    `Desde el punto de vista de la distribución geográfica, ${centroTop[0]} concentra el ${fmtPct(pctCentroTop)} de los leads activos. Las instalaciones con menor volumen representan una oportunidad de mejora mediante campañas digitales geolocalizadas.`
  );
  y += 3;
  y = paragraph(doc, y,
    `Respecto a los canales de captación, este mes han entrado ${totalMes} nuevos leads en total. El canal ${canalMasActivo} es el más activo con ${Math.max(emailMes, custMes, metaMes)} contactos.`
  );

  // 4 tarjetas KPI grandes al pie de la página 1
  const kpiY = 220;
  const kpiW = 42;
  const kpiH = 26;
  const kpiGap = 4;
  const kpiStartX = (210 - (4 * kpiW + 3 * kpiGap)) / 2;

  const kpis = [
    { value: `${leadsCartera}`, label: 'LEADS EN CARTERA', sub: 'Clientes potenciales activos', color: PALETTE.azulDimo },
    { value: `${comercialesActivos}`, label: 'COMERCIALES ACTIVOS', sub: 'Equipo asignado', color: PALETTE.azulClaro },
    { value: `${tasaConv}%`, label: 'TASA DE CONVERSION', sub: `${tasaConv > 0 ? '1 de cada ' + Math.round(100 / tasaConv) + ' leads compra' : 'Sin datos aún'}`, color: [66, 167, 110] },
    { value: `${kpiTiempo}`, label: 'TIEMPO DE RESPUESTA', sub: 'Media AutoLeads', color: PALETTE.amarillo },
  ];

  doc.setFillColor(246, 249, 253);
  doc.roundedRect(kpiStartX - 2, kpiY - 2, 4 * kpiW + 3 * kpiGap + 4, kpiH + 4, 2, 2, 'F');

  kpis.forEach((k, i) => {
    const x = kpiStartX + i * (kpiW + kpiGap);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...k.color);
    doc.text(k.value, x + kpiW / 2, kpiY + 10, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...PALETTE.grisTexto);
    doc.text(k.label, x + kpiW / 2, kpiY + 15, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...PALETTE.grisSuave);
    doc.text(k.sub, x + kpiW / 2, kpiY + 19, { align: 'center' });
  });

  // ================================================================
  // ============================ PÁGINA 2 ==========================
  // ================================================================
  doc.addPage();
  addPageFrame(doc, 2);

  sectionTitle(doc, 20, PALETTE.azulDimo, 'Actividad del mes por canal de entrada');

  // 3 donuts lado a lado
  const dY = 38;
  const dW = 56; const dH = 42;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PALETTE.grisTexto);
  doc.text('Web y portales', 14 + dW / 2, dY - 2, { align: 'center' });
  doc.text('Portal Stellantis', 14 + dW + 10 + dW / 2, dY - 2, { align: 'center' });
  doc.text('Facebook/Instagram', 14 + 2 * (dW + 10) + dW / 2, dY - 2, { align: 'center' });

  doc.addImage(imgDonutEmail, 'PNG', 14, dY, dW, dH);
  doc.addImage(imgDonutCust, 'PNG', 14 + dW + 10, dY, dW, dH);
  doc.addImage(imgDonutMeta, 'PNG', 14 + 2 * (dW + 10), dY, dW, dH);

  // Tabla resumen entradas por periodo
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PALETTE.grisTexto);
  doc.text('Resumen de entradas por periodo', 14, 98);

  doc.autoTable({
    startY: 102,
    head: [['Canal', 'Hoy', 'Esta semana', 'Este mes']],
    body: [
      ['Web y portales', emailHoy, emailSem, emailMes],
      ['Portal Stellantis', custHoy, custSem, custMes],
      ['Facebook / Instagram', metaHoy, metaSem, metaMes],
      [{ content: 'TOTAL', styles: { fontStyle: 'bold' } }, { content: totalHoy, styles: { fontStyle: 'bold' } }, { content: totalSem, styles: { fontStyle: 'bold' } }, { content: totalMes, styles: { fontStyle: 'bold' } }],
    ],
    theme: 'grid',
    headStyles: { fillColor: PALETTE.azulDimo, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' },
    },
    styles: { fontSize: 10, cellPadding: 3 },
    alternateRowStyles: { fillColor: [245, 248, 253] },
    margin: { left: 14, right: 14 },
  });

  // Nota informativa en banda azul
  let noteY = doc.lastAutoTable.finalY + 8;
  doc.setFillColor(...PALETTE.bandaAzul);
  doc.rect(14, noteY, 182, 20, 'F');
  doc.setDrawColor(...PALETTE.azulDimo);
  doc.setLineWidth(1.5);
  doc.line(14, noteY, 14, noteY + 20);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...PALETTE.grisTexto);
  doc.text(
    doc.splitTextToSize(
      `Los ${leadsCartera} leads en cartera acumulan contactos de semanas y meses anteriores que siguen activos en el proceso de venta. Los datos de hoy, semana y mes corresponden a nuevas entradas en cada periodo, no al total acumulado.`,
      175
    ),
    18, noteY + 6
  );

  // ================================================================
  // ============================ PÁGINA 3 ==========================
  // ================================================================
  doc.addPage();
  addPageFrame(doc, 3);

  sectionTitle(doc, 20, [66, 167, 110], '2. Tipo de vehículo demandado');

  let p3y = 38;
  p3y = paragraph(doc, p3y,
    `De los ${vn + vo} leads totales, ${vn} (${fmtPct(pctVN)}) buscan un vehículo nuevo (VN) y ${vo} (${fmtPct(pctVO)}) optan por vehículo de ocasión (VO). Esta proporción es coherente con el perfil digital de nuestros clientes, atraídos principalmente por las campañas de gama nueva Stellantis.`,
    { size: 10 }
  );

  // Donut VN/VO + tabla lado a lado
  doc.addImage(imgDonutVNVO, 'PNG', 14, p3y + 4, 80, 60);
  doc.autoTable({
    startY: p3y + 6,
    head: [['Tipo de vehiculo', 'Leads', '%']],
    body: [
      ['Vehiculo Nuevo (VN)', vn, fmtPct(pctVN)],
      ['Vehiculo de Ocasion (VO)', vo, fmtPct(pctVO)],
      [{ content: 'TOTAL', styles: { fontStyle: 'bold' } }, { content: vn + vo, styles: { fontStyle: 'bold' } }, { content: '100%', styles: { fontStyle: 'bold' } }],
    ],
    theme: 'grid',
    headStyles: { fillColor: PALETTE.azulDimo, textColor: [255, 255, 255], halign: 'center' },
    columnStyles: { 0: { halign: 'left' }, 1: { halign: 'center' }, 2: { halign: 'center' } },
    styles: { fontSize: 10, cellPadding: 3 },
    alternateRowStyles: { fillColor: [245, 248, 253] },
    margin: { left: 100, right: 14 },
  });

  let p3y2 = Math.max(p3y + 70, doc.lastAutoTable.finalY + 8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PALETTE.grisTexto);
  doc.text('Desglose AutoLeads por tipo (este mes)', 14, p3y2);

  doc.autoTable({
    startY: p3y2 + 4,
    head: [['Tipo', 'Leads asignados', '% sobre AutoLeads del mes']],
    body: [
      ['Vehiculo Nuevo (VN)', autoVN, fmtPct(pctAutoVN)],
      ['Vehiculo de Ocasion (VO)', autoVO, fmtPct(pctAutoVO)],
    ],
    theme: 'grid',
    headStyles: { fillColor: PALETTE.azulDimo, textColor: [255, 255, 255], halign: 'center' },
    columnStyles: { 0: { halign: 'left' }, 1: { halign: 'center' }, 2: { halign: 'center' } },
    styles: { fontSize: 10, cellPadding: 3 },
    alternateRowStyles: { fillColor: [245, 248, 253] },
    margin: { left: 14, right: 14 },
  });

  // Nota ámbar
  let noteY3 = doc.lastAutoTable.finalY + 8;
  doc.setFillColor(...PALETTE.bandaAmbar);
  doc.rect(14, noteY3, 182, 18, 'F');
  doc.setDrawColor(...PALETTE.amarillo);
  doc.setLineWidth(1.5);
  doc.line(14, noteY3, 14, noteY3 + 18);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...PALETTE.grisTexto);
  doc.text(
    doc.splitTextToSize(
      `La demanda de vehiculo nuevo ${pctVN > 90 ? 'supera el 90%' : 'se situa en el ' + fmtPct(pctVN)} en todos los canales. Se recomienda reforzar el catalogo de vehiculos de ocasion en los portales digitales para ampliar el abanico de clientes potenciales y diversificar la cartera.`,
      175
    ),
    18, noteY3 + 6
  );

  // ================================================================
  // ============================ PÁGINA 4 ==========================
  // ================================================================
  doc.addPage();
  addPageFrame(doc, 4);

  sectionTitle(doc, 20, PALETTE.verde, '3. Distribución de leads por instalación');

  let p4y = 38;
  p4y = paragraph(doc, p4y,
    `${centroTop[0]} concentra la mayor actividad con ${centroTop[1]} leads (${fmtPct(pctCentroTop)} del total). Las instalaciones con menor volumen presentan actividad reducida, lo que puede indicar una oportunidad de mejora en la captación digital de estas ubicaciones.`,
    { size: 10, bold: false }
  );

  // Gráfica horizontal
  doc.addImage(imgBarH, 'PNG', 14, p4y + 4, 180, 70);

  // Tabla
  const nivelActividad = (leads) => {
    if (leads >= 80) return { t: 'Alta', c: [66, 167, 110] };
    if (leads >= 30) return { t: 'Media', c: [245, 197, 66] };
    return { t: 'Baja', c: [235, 139, 72] };
  };

  doc.autoTable({
    startY: p4y + 80,
    head: [['Instalacion', 'Leads', '% del total', 'Actividad']],
    body: centrosDef.map(([nombre, leads]) => {
      const nv = nivelActividad(leads);
      return [nombre, leads, fmtPct(pct(leads, centrosTotal)), { content: `●  ${nv.t}`, styles: { textColor: nv.c } }];
    }),
    theme: 'grid',
    headStyles: { fillColor: PALETTE.azulDimo, textColor: [255, 255, 255], halign: 'center' },
    columnStyles: { 0: { halign: 'left' }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' } },
    styles: { fontSize: 10, cellPadding: 3 },
    alternateRowStyles: { fillColor: [245, 248, 253] },
    margin: { left: 14, right: 14 },
  });

  let noteY4 = doc.lastAutoTable.finalY + 6;
  const centrosBajos = centrosDef.filter(([, v]) => v < 30);
  const sumaBajos = centrosBajos.reduce((s, c) => s + c[1], 0);
  doc.setFillColor(240, 250, 244);
  doc.rect(14, noteY4, 182, 18, 'F');
  doc.setDrawColor(...PALETTE.verde);
  doc.setLineWidth(1.5);
  doc.line(14, noteY4, 14, noteY4 + 18);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...PALETTE.grisTexto);
  doc.text(
    doc.splitTextToSize(
      `${centrosBajos.length >= 2 ? centrosBajos.slice(0, 2).map((c) => c[0]).join(' y ') : 'Los centros con menor actividad'} suman ${sumaBajos} leads (${fmtPct(pct(sumaBajos, centrosTotal))} del total). Se recomienda revisar su presencia en Google Maps, portales de automocion y activar campanas de Meta Ads geolocalizadas para estas zonas.`,
      175
    ),
    18, noteY4 + 6
  );

  // ================================================================
  // ============================ PÁGINA 5 ==========================
  // ================================================================
  doc.addPage();
  addPageFrame(doc, 5);

  sectionTitle(doc, 20, PALETTE.azulDimo, '4. Actividad por canal y marca');

  let p5y = 38;
  p5y = paragraph(doc, p5y,
    `Email Leads aporta ${emailMes} contactos este mes, Customer 360 aporta ${custMes} leads de marcas concretas, Meta Leads suma ${metaMes} contactos y AutoLeads ha procesado ${autoMes}. En conjunto, se han captado ${totalMes} nuevos leads solo este mes.`
  );

  doc.addImage(imgBarV, 'PNG', 14, p5y + 4, 180, 65);

  // Tabla Email origenes (izq)
  const emailRows = emailOrigenes.map(([l, v]) => [l, v, fmtPct(pct(v, emailTotal))]);
  doc.autoTable({
    startY: p5y + 75,
    head: [['Fuente Email', 'Leads', '%']],
    body: emailRows,
    theme: 'grid',
    headStyles: { fillColor: PALETTE.azulDimo, textColor: [255, 255, 255], halign: 'center' },
    columnStyles: { 0: { halign: 'left' }, 1: { halign: 'center' }, 2: { halign: 'center' } },
    styles: { fontSize: 9.5, cellPadding: 2.5 },
    alternateRowStyles: { fillColor: [245, 248, 253] },
    tableWidth: 85,
    margin: { left: 14 },
  });

  // Tabla Red social (der)
  const metaRows = metaOrigenes.map(([l, v]) => [l, v, fmtPct(pct(v, metaTotal))]);
  doc.autoTable({
    startY: p5y + 75,
    head: [['Red social', 'Leads', '%']],
    body: metaRows,
    theme: 'grid',
    headStyles: { fillColor: PALETTE.azulDimo, textColor: [255, 255, 255], halign: 'center' },
    columnStyles: { 0: { halign: 'left' }, 1: { halign: 'center' }, 2: { halign: 'center' } },
    styles: { fontSize: 9.5, cellPadding: 2.5 },
    alternateRowStyles: { fillColor: [245, 248, 253] },
    tableWidth: 85,
    margin: { left: 111 },
  });

  let p5y2 = Math.max(
    (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY : p5y + 130,
    p5y + 130
  ) + 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PALETTE.grisTexto);
  doc.text('Customer Leads por marca (este mes)', 14, p5y2);
  doc.addImage(imgBarMarca, 'PNG', 14, p5y2 + 4, 180, 52);

  // ================================================================
  // ============================ PÁGINA 6 ==========================
  // ================================================================
  doc.addPage();
  addPageFrame(doc, 6);

  sectionTitle(doc, 20, PALETTE.amarillo, '5. Conclusiones y recomendaciones');

  let p6y = 38;
  p6y = paragraph(doc, p6y,
    'A continuación se recogen los hallazgos más relevantes del periodo y las acciones recomendadas para la dirección del grupy:'.replace('grupy', 'grupo'),
    { size: 10 }
  );

  // Generación dinámica de bullets
  const ratio = comercialesActivos > 0 ? Math.round(leadsCartera / comercialesActivos) : 0;
  const bullets = [];

  // Bullet 1 - volumen
  if (comercialesActivos > 0) {
    bullets.push({
      color: PALETTE.verde,
      icon: '✓',
      title: `Volumen de leads ${ratio < 40 ? 'en niveles saludables' : 'elevado — revisar capacidad'}`,
      body: `Con ${leadsCartera} leads activos y ${comercialesActivos} comerciales, la ratio es de ${ratio} leads por comercial, ${ratio < 40 ? 'un nivel manejable que permite atencion personalizada sin saturar al equipo de ventas' : 'lo que puede dificultar el seguimiento individualizado'}.`,
    });
  }

  // Bullet 2 - tasa conversión
  if (tasaConv > 0) {
    if (tasaConv > 8) {
      bullets.push({
        color: PALETTE.verde,
        icon: '✓',
        title: `Tasa de conversion del ${tasaConv}% — por encima de la media del sector`,
        body: `El sector de automocion online registra habitualmente tasas entre el 5% y el 8%. Situarnos en el ${tasaConv}% indica que la calidad de los leads captados es alta y que el equipo comercial esta realizando un buen seguimiento.`,
      });
    } else if (tasaConv >= 5) {
      bullets.push({
        color: PALETTE.azulClaro,
        icon: '✓',
        title: `Tasa de conversion del ${tasaConv}% — dentro de la media sectorial`,
        body: `La conversion actual se situa dentro de la horquilla habitual del sector (5-8%). Hay margen de mejora ajustando el seguimiento comercial y la calidad de captacion.`,
      });
    } else {
      bullets.push({
        color: [235, 139, 72],
        icon: '!',
        title: `Tasa de conversion del ${tasaConv}% — por debajo de la media sectorial`,
        body: `La conversion actual esta por debajo de la media del sector (5-8%). Se recomienda revisar el proceso de seguimiento comercial y la calidad de los leads captados.`,
      });
    }
  }

  // Bullet 3 - AutoLeads
  if (auto.ultimaAsignacion && auto.ultimaAsignacion !== 'N/A') {
    bullets.push({
      color: PALETTE.azulClaro,
      icon: '✓',
      title: 'Sistema AutoLeads operativo con respuesta rapida',
      body: `La ultima asignacion automatica se realizo el ${auto.ultimaAsignacion}. Un tiempo de respuesta de ${kpiTiempo} mejora la experiencia del cliente y reduce el abandono del proceso de compra.`,
    });
  }

  // Bullet 4 - concentración centro top
  if (pctCentroTop > 30 && centrosTotal > 0) {
    bullets.push({
      color: PALETTE.amarillo,
      icon: '!',
      title: `${centroTop[0]} concentra el ${fmtPct(pctCentroTop)} de los leads`,
      body: `Aunque es la instalacion mas grande, esta concentracion puede generar desequilibrios en la carga de trabajo. Se recomienda reforzar la captacion en las otras instalaciones mediante campanas localizadas.`,
    });
  }

  // Bullet 5 - centros bajos
  if (centrosBajos.length >= 1 && sumaBajos < centrosTotal * 0.1) {
    bullets.push({
      color: [235, 139, 72],
      icon: '!',
      title: `${centrosBajos.slice(0, 2).map((c) => c[0]).join(' y ')} con actividad baja`,
      body: `Estas instalaciones suman solo ${sumaBajos} leads (${fmtPct(pct(sumaBajos, centrosTotal))} del total). Se recomienda revisar su visibilidad en Google Maps, Coches.net y activar campanas de Meta Ads con segmentacion geografica especifica.`,
    });
  }

  // Bullet fallback si no hay datos
  if (bullets.length === 0) {
    bullets.push({
      color: PALETTE.grisSuave,
      icon: 'i',
      title: 'Sin datos suficientes en el periodo',
      body: 'No se registran leads activos en la ventana actual. Verifica los flujos de n8n (Email Leads, Customer360, Meta Leads) y la conectividad con Supabase antes de generar nuevas recomendaciones.',
    });
  }

  bullets.forEach((b) => {
    const boxH = 22;
    doc.setFillColor(
      b.color[0] === PALETTE.verde[0] ? 240 : b.color[0] === PALETTE.amarillo[0] ? 254 : b.color[0] === PALETTE.azulClaro[0] ? 238 : 253,
      b.color[0] === PALETTE.verde[0] ? 250 : b.color[0] === PALETTE.amarillo[0] ? 248 : b.color[0] === PALETTE.azulClaro[0] ? 245 : 240,
      b.color[0] === PALETTE.verde[0] ? 244 : b.color[0] === PALETTE.amarillo[0] ? 225 : b.color[0] === PALETTE.azulClaro[0] ? 253 : 235,
    );
    doc.rect(14, p6y, 182, boxH, 'F');
    doc.setDrawColor(...b.color);
    doc.setLineWidth(1.8);
    doc.line(14, p6y, 14, p6y + boxH);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...b.color);
    doc.text(b.icon, 18, p6y + 6);

    doc.setFontSize(10);
    doc.setTextColor(...PALETTE.grisTexto);
    doc.text(b.title, 24, p6y + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...PALETTE.grisTexto);
    const bodyLines = doc.splitTextToSize(b.body, 168);
    doc.text(bodyLines, 24, p6y + 11);

    p6y += boxH + 3;
  });

  // Pie del informe
  p6y += 4;
  doc.setDrawColor(225, 229, 235);
  doc.line(14, p6y, 196, p6y);
  p6y += 5;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...PALETTE.grisSuave);
  doc.text(
    doc.splitTextToSize(
      `Informe generado automaticamente por el sistema CRM de Grupo Dimoauto. Fecha: ${hoy.getDate()} de ${mesES[hoy.getMonth()]} de ${hoy.getFullYear()}. Contacto: departamento de Marketing y Tecnologia.`,
      182
    ),
    14, p6y
  );

  // ==== Guardar ====
  const nombre = `Informe_CRM_Dimoauto_${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}.pdf`;
  doc.save(nombre);
};

// ---------- Componente del botón flotante ----------
const InformeCRMButton = ({ data }) => {
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);

  const onClick = async () => {
    if (generando) return;
    setError(null);
    setGenerando(true);
    try {
      await generarInforme(data);
    } catch (e) {
      console.error('Error generando informe:', e);
      setError(e.message || 'Error generando PDF');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={generando}
        title="Descargar Informe CRM en PDF"
        style={{
          position: 'fixed',
          right: '20px',
          bottom: '20px',
          zIndex: 9999,
          background: '#005187',
          color: '#ffffff',
          border: 'none',
          borderRadius: '999px',
          padding: '10px 18px',
          fontSize: '13px',
          fontWeight: 600,
          boxShadow: '0 6px 18px rgba(0,81,135,0.28)',
          cursor: generando ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.2s ease',
          opacity: generando ? 0.85 : 1,
        }}
        onMouseEnter={(e) => {
          if (!generando) {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 8px 22px rgba(0,81,135,0.35)';
            e.currentTarget.style.background = '#006aa8';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,81,135,0.28)';
          e.currentTarget.style.background = '#005187';
        }}
      >
        {generando ? (
          <>
            <span
              style={{
                width: '14px',
                height: '14px',
                border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: '#ffffff',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'spin-informe 0.8s linear infinite',
              }}
            />
            Generando…
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <polyline points="9 15 12 18 15 15" />
            </svg>
            Informe PDF
          </>
        )}
      </button>

      {error && (
        <div
          style={{
            position: 'fixed',
            right: '20px',
            bottom: '70px',
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #f5c2c7',
            color: '#842029',
            padding: '8px 14px',
            borderRadius: '6px',
            fontSize: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          {error}
        </div>
      )}

      <style>{`
        @keyframes spin-informe {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default InformeCRMButton;
